import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Printer } from "lucide-react";
import { api } from "@/lib/api";
import { MONTHS, SERVICE_TYPES, formatDate, setServiceTypes, statusOf, todayISO } from "@/lib/services";
import { LogoMark } from "@/components/app/Logo";

const CompanyBlock = ({ c }) => {
  if (!c?.company_name && !c?.responsible_name && !c?.phone) return null;
  const extra = [c.responsible_name && `Responsável: ${c.responsible_name}`, c.phone && `Tel.: ${c.phone}`].filter(Boolean).join(" · ");
  return (
    <div className="mb-5" data-testid="report-company">
      {c.company_name && <div className="font-display text-xl font-bold">{c.company_name}</div>}
      {extra && <div className="text-sm text-slate-600">{extra}</div>}
    </div>
  );
};

const Cell = ({ children, className = "" }) => <td className={`border-b border-slate-200 px-2 py-2 align-top ${className}`}>{children}</td>;

const TypeLabel = ({ type }) => (
  <span className="flex items-center gap-1.5 whitespace-nowrap">
    <span className="print-exact h-2.5 w-2.5 rounded-full" style={{ background: SERVICE_TYPES[type].hex }} />
    {SERVICE_TYPES[type].label}
  </span>
);

const ReportTable = ({ title, items, contacts, done, testid }) => (
  <section className="mt-8 break-inside-avoid-page" data-testid={testid}>
    <h2 className="mb-2 font-display text-lg font-bold">{title} <span className="font-mono text-sm text-slate-500">({items.length})</span></h2>
    {items.length === 0 ? <p className="text-sm text-slate-500">Nenhum lançamento.</p> : (
      <table className="w-full border-collapse text-left text-xs">
        <thead>
          <tr className="border-b-2 border-slate-800 text-[11px] uppercase tracking-wider text-slate-600">
            <th className="px-2 py-2">Data</th><th className="px-2 py-2">Cliente</th><th className="px-2 py-2">Telefone</th>
            <th className="px-2 py-2">Endereço</th><th className="px-2 py-2">Serviço</th>
            <th className="px-2 py-2">{done ? "Válido até" : "Situação"}</th><th className="px-2 py-2">Observações</th>
          </tr>
        </thead>
        <tbody>
          {items.map((s) => {
            const c = contacts[s.client_key] || {};
            return (
              <tr key={s.id} data-testid={`report-row-${s.id}`}>
                <Cell className="whitespace-nowrap font-mono">{formatDate(s.execution_date)}</Cell>
                <Cell className="font-semibold">{s.client_name}</Cell>
                <Cell className="whitespace-nowrap">{c.phone || "—"}</Cell>
                <Cell>{c.address || "—"}</Cell>
                <Cell><TypeLabel type={s.service_type} /></Cell>
                <Cell className="whitespace-nowrap">{done ? formatDate(s.expiry_date) : (statusOf(s, todayISO()).key === "vencido" ? "Vencido" : "Agendado")}</Cell>
                <Cell className="text-slate-600">{s.notes || ""}</Cell>
              </tr>
            );
          })}
        </tbody>
      </table>
    )}
  </section>
);

const Summary = ({ items }) => (
  <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="report-summary">
    {Object.entries(SERVICE_TYPES).map(([k, t]) => (
      <div key={k} className="rounded-lg border border-slate-300 p-3">
        <div className="flex items-center gap-1.5 text-xs text-slate-600"><span className="print-exact h-2.5 w-2.5 rounded-full" style={{ background: t.hex }} />{t.short}</div>
        <div className="mt-1 font-display text-2xl font-bold" data-testid={`report-count-${k}`}>{items.filter((s) => s.service_type === k).length}</div>
      </div>
    ))}
  </div>
);

export default function MonthReport() {
  const { year, month } = useParams();
  const m = Number(month);
  const prefix = `${year}-${String(m).padStart(2, "0")}`;
  const [items, setItems] = useState(null);
  const [contacts, setContacts] = useState({});
  const [company, setCompany] = useState(null);

  useEffect(() => {
    api.get("/settings/company").then((r) => setCompany(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([api.get("/services", { params: { year } }), api.get("/clients"), api.get("/service-types")]).then(([s, c, t]) => {
      setServiceTypes(t.data);
      setItems(s.data.filter((x) => x.execution_date.startsWith(prefix)));
      setContacts(Object.fromEntries(c.data.map((x) => [x.key, x])));
    }).catch(() => setItems([]));
  }, [year, prefix]);

  if (!(m >= 1 && m <= 12)) return <p className="p-10">Mês inválido.</p>;
  if (!items) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const done = items.filter((s) => s.status === "realizado");
  const scheduled = items.filter((s) => s.status === "agendado");

  return (
    <div className="report-page min-h-screen bg-white text-slate-900" data-testid="month-report">
      <div className="mx-auto max-w-5xl p-6 sm:p-10 print:max-w-none print:p-0">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b-2 border-slate-900 pb-4">
          <div>
            <CompanyBlock c={company} />
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500"><LogoMark className="print-exact h-4 w-4" check="#0F172A" /> Ciclo Certo · Relatório mensal</div>
            <h1 className="mt-1 font-display text-4xl font-extrabold tracking-tight" data-testid="report-title">{MONTHS[m - 1]} {year}</h1>
            <p className="mt-1 text-sm text-slate-600">{done.length} realizado(s) · {scheduled.length} agendado(s) · emitido em {formatDate(todayISO())}</p>
          </div>
          <button onClick={() => window.print()} className="no-print flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-700" data-testid="report-print-button">
            <Printer className="h-4 w-4" /> Imprimir
          </button>
        </div>
        <Summary items={items} />
        <ReportTable title="Serviços realizados" items={done} contacts={contacts} done testid="report-done-table" />
        <ReportTable title="Serviços agendados" items={scheduled} contacts={contacts} testid="report-scheduled-table" />
      </div>
    </div>
  );
}
